/**
 * Step 1: User credentials (account name, account ID).
 * For Zerodha/Groww shows OAuth vs TOTP choice at the top.
 */

import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  FormLabel,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Stack
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { AuthMode } from './brokerRegistrationConfig';
import type { BrokerMetadata } from './types';

export interface WizardStep1Data {
  accountName: string;
  accountId: string;
  authMode: AuthMode;
}

interface BrokerRegistrationWizardStep1Props {
  broker: BrokerMetadata;
  data: WizardStep1Data;
  onChange: (data: WizardStep1Data) => void;
  authModes: AuthMode[];
  accountIdLabel: string;
  accountIdPlaceholder: string;
  accountIdHelperText: string;
  accountIdMinLength: number;
  error?: string;
  disabled?: boolean;
}

export function BrokerRegistrationWizardStep1({
  broker,
  data,
  onChange,
  authModes,
  accountIdLabel,
  accountIdPlaceholder,
  accountIdHelperText,
  accountIdMinLength,
  error,
  disabled = false,
}: BrokerRegistrationWizardStep1Props) {
  const handleChange = (field: keyof WizardStep1Data) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = e.target.value;
    if (field === 'accountId' && broker.broker_type.toLowerCase() !== 'dhan' && broker.broker_type.toLowerCase() !== 'groww') {
      onChange({ ...data, [field]: value.toUpperCase() });
    } else {
      onChange({ ...data, [field]: value });
    }
  };

  const showAuthChoice = authModes.length === 2;

  const shortName = broker.display_name
    ? broker.display_name.split(' ')[0]
    : broker.broker_type.charAt(0).toUpperCase() + broker.broker_type.slice(1);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {showAuthChoice && (
        <FormControl component="fieldset">
          <FormLabel
            component="legend"
            sx={{
              mb: 2,
              fontWeight: 700,
              color: '#04377b',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Connection Method
          </FormLabel>
          <Grid container spacing={2} sx={{ position: 'relative', alignItems: 'center' }}>
            {[
              {
                value: 'oauth' as AuthMode,
                title: 'Broker Login',
                desc: 'Sign on broker website',
              },
              {
                value: 'totp' as AuthMode,
                title: 'Automated (TOTP)',
                desc: 'Sign with automated TOTP',
              },
            ].map((item, index) => (
              <React.Fragment key={item.value}>
                {index === 1 && (
                  <Grid item xs={12} sm={1} sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    height: { xs: '20px', sm: '100%' },
                    my: { xs: 1, sm: 0 },
                    px: { sm: 0 }
                  }}>
                    <Box sx={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', fontSize: '0.65rem' }}>
                        OR
                      </Typography>
                    </Box>
                    {broker.broker_type.toLowerCase() !== 'zerodha' && (
                      <Box sx={{
                        display: { xs: 'none', sm: 'block' },
                        position: 'absolute',
                        left: '50%',
                        top: { sm: '-60px' }, // Extend line to top
                        bottom: { sm: '-60px' }, // Extend line to bottom
                        width: '1px',
                        bgcolor: 'divider',
                        zIndex: 1,
                        transform: 'translateX(-50%)'
                      }} />
                    )}
                  </Grid>
                )}
                <Grid item xs={12} sm={5.5}>
                  <Card
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      border: '2px solid',
                      borderColor: data.authMode === item.value ? '#04377b' : 'rgba(0, 0, 0, 0.05)',
                      bgcolor: data.authMode === item.value ? 'rgba(4, 55, 123, 0.02)' : 'transparent',
                      transition: 'all 0.2s ease-in-out',
                      position: 'relative',
                      overflow: 'visible',
                    }}
                  >
                    {data.authMode === item.value && (
                      <CheckCircleIcon
                        sx={{
                          position: 'absolute',
                          top: -10,
                          right: -10,
                          color: '#04377b',
                          bgcolor: 'white',
                          borderRadius: '50%',
                          zIndex: 1,
                        }}
                      />
                    )}
                    <CardActionArea
                      disabled={disabled}
                      onClick={() => onChange({ ...data, authMode: item.value })}
                      sx={{ p: 2, height: '100%', borderRadius: 3 }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Box sx={{ minWidth: 0, overflow: 'hidden', textAlign: 'center' }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{
                              mb: 0.25,
                              fontSize: '0.8125rem',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.7rem',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.2
                            }}
                          >
                            {item.desc}
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </FormControl>
      )}

      <Stack spacing={3}>
        <TextField
          label="Account Name"
          value={data.accountName}
          onChange={handleChange('accountName')}
          placeholder="e.g., My Primary Trading Account"
          fullWidth
          required
          size="small"
          disabled={disabled}
          error={!!error && (error.includes('Account name') || error.includes('name'))}
          helperText={error && (error.includes('Account name') || error.includes('name')) ? error : 'A friendly name to identify this account'}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
            '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.8125rem' },
            '& .MuiInputBase-input::placeholder': { fontSize: '0.8125rem', opacity: 0.7 },
          }}
        />
        <TextField
          label={
            accountIdLabel.includes(shortName)
              ? accountIdLabel
              : `${shortName} ${accountIdLabel}`
          }
          value={data.accountId}
          onChange={handleChange('accountId')}
          placeholder={
            accountIdPlaceholder.toLowerCase().includes(shortName.toLowerCase())
              ? accountIdPlaceholder
              : `Enter your ${accountIdLabel.toLowerCase().includes(shortName.toLowerCase()) ? '' : shortName.concat(' ')}${accountIdLabel}`
          }
          fullWidth
          required
          size="small"
          disabled={disabled}
          inputProps={{
            minLength: accountIdMinLength,
            maxLength: 100,
          }}
          error={
            !!error &&
            (error.includes('Account ID') ||
              error.includes('User ID') ||
              error.includes('Client ID') ||
              error.includes('Fyers') ||
              error.includes('required'))
          }
          helperText={
            error &&
              (error.includes('Account ID') ||
                error.includes('User ID') ||
                error.includes('Client ID') ||
                error.includes('Fyers') ||
                error.includes('required'))
              ? error
              : accountIdHelperText
          }
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
            '& .MuiInputLabel-root': { fontWeight: 600, fontSize: '0.8125rem' },
            '& .MuiInputBase-input::placeholder': { fontSize: '0.8125rem', opacity: 0.7 },
          }}
        />
      </Stack>
    </Box>
  );
}
